/**
 * Shared next-payday calculation. IncomeView and BudgetsView both show a
 * "next payday" figure; this is the single implementation so the two tabs
 * can't disagree about the date or the amount.
 */

export interface PaydaySource {
  amount: number
  frequency?: string | null
  category?: string
  /** Start/creation date of the source (YYYY-MM-DD) — day-of-month fallback. */
  date?: string
  /** Explicit payday configured by the user (1-31). */
  dayOfMonth?: number
  isActive?: boolean
}

export interface PaydayInfo {
  nextPayday: Date
  /** Sum of the selected salary sources' per-payment amounts. */
  nextPaydayAmount: number
  daysUntilNextPayday: number
}

// new Date(y, m, 31) overflows into the next month when m has fewer days
// (Jan 31 + 1 month → Mar 3), so clamp the day to the month's length.
function dateWithClampedDay(year: number, monthIndex: number, day: number): Date {
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate()
  return new Date(year, monthIndex, Math.min(day, daysInMonth))
}

/**
 * Next payday for a set of income sources. Prefers sources the user
 * categorized as salary; when none is marked, falls back to the largest
 * recurring monthly income. Paused sources are ignored. The payday stays in
 * the current month while it is still ahead, and rolls to the next month
 * otherwise. With no usable source it falls back to the end of the month.
 */
export function nextPaydayInfo(sources: ReadonlyArray<PaydaySource>, now: Date = new Date()): PaydayInfo {
  const today = new Date(now)
  today.setHours(0, 0, 0, 0)

  const active = sources.filter((s) => s.isActive !== false)
  let salarySources = active.filter((s) => s.category === "salary")
  if (salarySources.length === 0) {
    const monthly = active.filter((s) => s.frequency === "monthly" || !s.frequency)
    if (monthly.length > 0) {
      salarySources = [monthly.reduce((max, s) => (s.amount > max.amount ? s : max))]
    }
  }

  let nextPayday: Date
  if (salarySources.length > 0) {
    const src = salarySources[0]
    // Read the day straight from the "YYYY-MM-DD" string — new Date(str)
    // parses as UTC, so .getDate() lands a day early west of Greenwich
    // (see scheduleDaysFromDate in localDate.ts for the same trap).
    const explicitDay =
      src.dayOfMonth && src.dayOfMonth >= 1 && src.dayOfMonth <= 31
        ? src.dayOfMonth
        : (src.date ? Number(src.date.split("-")[2]) : 0) || 25
    nextPayday = dateWithClampedDay(today.getFullYear(), today.getMonth(), explicitDay)
    if (nextPayday <= today) {
      nextPayday = dateWithClampedDay(today.getFullYear(), today.getMonth() + 1, explicitDay)
    }
  } else {
    // No salary data → end of the current month.
    nextPayday = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  }

  const daysUntilNextPayday = Math.max(0, Math.ceil((nextPayday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)))
  const nextPaydayAmount = salarySources.reduce((sum, s) => sum + s.amount, 0)

  return { nextPayday, nextPaydayAmount, daysUntilNextPayday }
}
